import React, { StrictMode, useState, useEffect, useMemo, useCallback } from "react";

import { ThemeProvider } from "@pixel/core";
import ActionGroup, { ActionItemBase } from '@pixel/core/ActionGroup';
import Box from "@pixel/core/Box";
import Chip from "@pixel/core/Chip";
import Link from "@pixel/core/Link";
import DataTable from "@pixel/core/DataTable";
import Paper from "@pixel/core/Paper";
import Typography from "@pixel/core/Typography";
import LabelValueBase from "@pixel/core/LabelValueBase";
import LabelValueLayout from "@pixel/core/LabelValueLayout";
import ListBase, {
    ListAppliedSearchFilterProps,
    ListSearchChangeProps,
    SortOrderProps,
    ListSearchFilterSelectMultiProps,
    ListSearchFilterSelectSingleProps,
    OnSelectAllChangeEvent
} from '@pixel/core/ListBase';
import SnackbarServiceProvider from "@pixel/core/SnackbarService";
import StatusMessage from '@pixel/core/StatusMessage'
import ListItemCards from '@pixel/core/ListItemCards';

import { createInternalTheme } from "@pixel/internal/styles";
import LayoutRoot from "@pixel/internal/LayoutRoot";
import LayoutRootSection from "@pixel/internal/LayoutRootSection";

import ArticleIcon from "@mui/icons-material/Article";
import TableChartIcon from "@mui/icons-material/TableChart";
import Moment from "moment";

import { ICandidatePayload, ICandidateResultData, IColumnHeaderList, SelectedNodeItem, IActionObj, ICandidateData } from "../../types/candidates";
import {
    transformCandidateResultData,
    filterCandName,
    filterAppStatus,
    validateDuplicateCandidates,
    transformFilterData,
    setActiveFilterChip,
    filterDuplicateFilters,
    setFilterAppStatusFilterValues,
    filterOverallCompatibility,
    setFilterOverallCompatibility
} from "../../utils/candidateResultTransform";

import { LocalizationProvider } from '../../contexts/Localization/LocalizationProvider';
import { LocalizationValues, LocalizedText } from '../../contexts/Localization/LocalizationTypes';
import DomainProvider, { DomainProviderContextValue } from '../../contexts/Domain/DomainProvider'
import JobDetailsProvider from "../../contexts/JobDetails";

import PreviewCandidateModal from "./PreviewCandidate";
import CandidateServiceProvider from '../../contexts/CandidateService'
import CandidateListContext from '../../contexts/candidate/candidateListContext';
import CandidateActionsModal from './CandidateActionsModal';
import { getColumnValueFromSearchNode } from "../../utils/searchResultUtils";
import { ListBaseDefaultLoading } from "../ListBaseDefaultLoading";
import { QueryClient } from "@tanstack/query-core";
import { QueryClientProvider } from "@tanstack/react-query";
import LocalizedTypography from "../LocalizedTypography";
import { CandidateWorkflowState } from '../../hooks/useChangeStatus';
import {styled} from "@pixel/core/styles";

const queryClient = new QueryClient()

declare global {
  interface Window { jvUrls: DomainProviderContextValue, candidateStickyColumnsAndHeadersEnabled: boolean, enableCandidateRanking: boolean, enableJvTalentFit: boolean }
}

// will sort by this column order, first valid column will be the default
const DEFAULT_SORT_PRIORITIES = [
  'lastActivity',
  'fullName',
]

export const App = (props: any) => {
    const reqsEId = sessionStorage.getItem("reqsId");

    const modalStyle = {

      itemStyle: {
        p: 0,
        m: 0,
        display: "list-item",
      },

      listStyle: {
        width: "100%",
        maxHeight: 275,
        overflow: "auto",
        listStyle: "disc outside none",
        display: "list-item",
        pl: 3,
        mt: 2,
        bgcolor: "background.paper",
      },
    };

    const TalentFitBadge = styled('div')({
        textTransform: 'uppercase',
        backgroundColor: 'white',
        border: '1px solid #197B1E',
        borderRadius: 16,
        fontWeight: 'bold',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 7px'
    })

    const CandidateInfoDetails = styled('div')({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        paddingTop: '16px',
        paddingBottom: '16px',
        width: '70%',
    })


    const CandidatePayloadData: ICandidatePayload = useMemo(() => ({
        keywordSearch: {
            searchNameOnly: false,
            searchNotesOnly: false,
            searchMessagesOnly: false,
            searchResumeOnly: false,
            exactMatch: false,
            typeAhead: false,
        },
        recruiters: [],
        hiringManagers: [],
        department: [],
        jobLocation: [],
        region: [],
        category: [],
        companies: [],
        jobTypeId: [],
        candidateCountry: [],
        postingType: [],
        availabilityState: [],
        sourceId: [],
        page: 0,
        jobId: [reqsEId],
        sortBy: [{
          fieldId: "lastActivity",
        }],
        order: "desc",
        searchType: "name",
        workflowStateId: [-1],
        overallCompatibility: []
    }), []);

    const searchFilterLastUpdated: ListSearchFilterSelectSingleProps = useMemo(() => ({
        id: "LastUpdated",
        label: props.translations.LAST_ACTIVITY,
        type: "single_select",
        options: [
            { value: "any", label: props.translations.I18N_ANY },
            { value: "1", label: props.translations.PAST_7_DAYS },
            { value: "2", label: props.translations.PAST_30_DAYS },
            { value: "3", label: props.translations.PAST_90_DAYS }
        ]
    }), []);

    const searchFilterCandState: ListSearchFilterSelectSingleProps = useMemo(() => ({
        id: "candidateState",
        label: props.translations.CANDIDATE_STATUS,
        type: "single_select",
        options: [
            { value: "all", label: props.translations.I18N_ALL },
            { value: "active", label: "Active" },
        ]
    }), []);

    const searchFitlerAppStatus: ListSearchFilterSelectMultiProps = useMemo(() => ({
        id: "AppStatus",
        label: props.translations.APPLICATION_STATUS,
        type: "multi_select",
        options: filterAppStatus
    }), []);

    const searchFitlerCandName: ListSearchFilterSelectMultiProps = useMemo(() => ({
        id: "candidateName",
        label: props.translations.CANDIDATE_NAME,
        type: "single_select",
        options: filterCandName
    }), []);

    const appliedSearchFilterStatus = useMemo(() => ({
        searchFilterId: "candidateState",
        displayLabel: props.translations.CANDIDATE_STATUS + ': Active',
        filterValues: [{ value: "active", label: "Active" }],
    }), []);

    const searchFilterTalentFit: ListSearchFilterSelectSingleProps = useMemo(() => ({
        id: "overallCompatibility",
        label: props.translations.TALENT_FIT,
        type: "single_select",
        options: filterOverallCompatibility
    }), []);

    const [appliedSearchFilters, setAppliedSearchFilters] = useState<Array<ListAppliedSearchFilterProps>>([appliedSearchFilterStatus]);
    const searchFilters = [searchFilterLastUpdated,
        searchFitlerAppStatus,
        searchFitlerCandName,
        searchFilterCandState,
        ...(window.enableCandidateRanking && window.enableJvTalentFit ? [searchFilterTalentFit] : [])];

    const sortOrderLastUpdated: SortOrderProps = useMemo(() => ({
        id: "lastActivity",
        label: props.translations.LAST_UPDATED,
        directions: ["desc", "asc"],
    }), []);

    const [sortOrders, setSortOrders] = useState<SortOrderProps>([]);
    const sortOrdersSet = useMemo(() => new Set(sortOrders.map((sortOrder: any) => sortOrder.id)), [sortOrders])
    const [appliedSortOrder, setAppliedSortOrder] = useState({
        sortOrder: sortOrderLastUpdated,
        direction: "desc"
    });

    const [CandPayload, setCandPayload] = useState<ICandidatePayload>(CandidatePayloadData);
    const [results, setCandidateResult] = useState<ICandidateResultData>({nodes: [], totalCount: 0 ,maxResults: 0});
    const [resultsLoading, setResultsLoading] = useState(true)
    const columnHeadersList: Array<IColumnHeaderList<Node>> = [];
    const [columnList, setColumnList] = useState<Array<IColumnHeaderList<Node>>>(columnHeadersList);
    const [actionsList, setActionsList] = useState([]);
    const [globalActionsList, setGlobalActionsList] = useState([]);
    const [selectType, setSelectType] = useState('single');
    const [selectedCandidate, setSelectedCandidate] = useState<Array<SelectedNodeItem['EId']>>([]);
    const [selectedCandidateDetails, setSelectedCandidateDetails] = useState<Array<SelectedNodeItem>>([]);
    const [validatedCandidateList, setValidatedCandidateList] = useState([]);
    const [open, setOpen] = useState(false);
    const [clickedAction, setClickedAction] = useState<IActionObj>({name: '', payLoadName: ''});
    const [isCandidateValidForMove, setIsCandidateValidForMove] = useState<boolean>(true);
    const [moveCandStatusBar, setMoveCandStatusBar] = useState<boolean>(false);
    const [actionsData, setActionsData] = useState({});
    const [permissions, setPermissions] = useState({});
    const [candidatePreview, setCandidatePreview] = useState(null)
    const [candidateCanChangeStatus, setCandidateCanChangeStatus] = useState(true);
    const [scrollEventAttachedToTable, setScrollEventAttachedToTable] = useState(false);

    function getDefaultSortColumn() {
      if (sortOrders.length <= 0) return null

      for(const defaultSortName of DEFAULT_SORT_PRIORITIES) {
        for (const sortOrder of sortOrders) {
          if (sortOrder.id === defaultSortName) {
            return sortOrder
          }
        }
      }

      return null
    }

    const defaultSortColumn = useMemo(() => getDefaultSortColumn(), [sortOrders])

    useEffect(() => {
      if (defaultSortColumn) {
        setAppliedSortOrder({
          sortOrder: defaultSortColumn,
          direction: "desc"
        })
      }
    }, [defaultSortColumn])

    useEffect(() => {
      async function fetchCandiadteApplications() {
        if (sortOrders.length === 0) return

        if (CandPayload.sortBy.length > 0 && !sortOrdersSet.has(CandPayload.sortBy[0].fieldId)) {
          const defaultSort = getDefaultSortColumn()
          CandPayload.sortBy[0].fieldId = defaultSort ? defaultSort.id : 'fullName'
        }

        try {
          setResultsLoading(true)
          await props.candidateService.searchApplications(
            CandPayload,
            (response: any) => {

              setPerPageCount(response.data.maxResults);
              setTotalCandidates(response.data.totalCount);
              sessionStorage.setItem('ngStorage-search_total_count', `${response.data.totalCount || 0}`);
              sessionStorage.setItem(
                'ngStorage-search_application_ids',
                JSON.stringify((response.data.applicationSearchResults || []).map((candidate: ICandidateData) => candidate.EId))
              );
              sessionStorage.setItem('ngStorage-appSearchFilters', JSON.stringify(CandPayload));
              setPagingContext({
                hideFirstLastActions: true,
                perPage: response.data.maxResults,
                page: CandPayload.page + 1,
                first:
                  response.data.totalCount > 0
                    ? CandPayload.page * response.data.maxResults + 1
                    : 0,
                last:
                  response.data.maxResults * CandPayload.page +
                  response.data.applicationSearchResults.length,
              });
              const res: ICandidateResultData = transformCandidateResultData(
                response.data
              );
              setCandidateResult(res);
              setResultsLoading(false)
            }
          );
        } catch (error) {
          console.log(error);
          setResultsLoading(false)
        }
      }
      fetchCandiadteApplications();
    }, [CandPayload, open, sortOrders, sortOrdersSet]);

    useEffect(() => {
        let columnWithSort: Array<object> = [];
        function createColumnList(headers: Array<any>) {
            let map: Array<IColumnHeaderList<ICandidateData>> = [];
            headers.sort((a: any, b: any) => a.searchId !== 'info' && a.displayOrder - b.displayOrder).forEach((column: any, index: number) => {
                column.canSort && columnWithSort.push(column);
                if (column.searchId != 'info') {
                    const columnCaption: IColumnHeaderList<ICandidateData> = {
                        id: column.searchId,
                        columnName: column.name,
                        minWidth: 150,
                        renderCell: (node) => node.EId,
                    }
                    if (!index && window.candidateStickyColumnsAndHeadersEnabled) { // Remove this if server-side `sticky` property is implemented.
                      columnCaption.sticky = true;
                    }
                    switch (column.searchId) {
                        case 'fullName': {
                            columnCaption.minWidth = 150;
                            columnCaption.renderCell = (node) => (
                                <div style={{display: 'flex', flexDirection: 'row'}}>
                                    <CandidateInfoDetails>
                                        <Link href={window.jvUrls.jhire + "/modules/candidates/search.html#/profile/" + node.EId} underline="hover" variant="body2" sx={{ fontWeight: 600, lineHeight: '24px' }}>{node.fullName}</Link>
                                        <Typography variant="caption">{node.email}</Typography>
                                        <Typography variant="caption">{node.homePhone}</Typography>
                                    </CandidateInfoDetails>
                                    {window.enableCandidateRanking && window.enableJvTalentFit && node?.overallCompatibility && (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <TalentFitBadge>
                                                <span className="icon icon-sparklies" style={{ fontSize: '16px' }} />
                                                <LocalizedTypography
                                                    variant="body1"
                                                    text={LocalizedText.TALENT_FIT}
                                                    color={'#197B1E'}
                                                    fontSize={'12px'}
                                                    fontWeight={'bold'}
                                                    letterSpacing={'0.08em'}
                                                    marginLeft={'5px'}
                                                />
                                            </TalentFitBadge>
                                        </div>
                                    )}
                                </div>
                            );
                            break;
                        }
                        case 'lastActivity': {
                            columnCaption.minWidth = 80;
                            columnCaption.renderCell = (node) => <Typography variant="body2">{Moment(node.lastActivity).format(window._jvselfUser.locale.userDate.shortDate().toUpperCase())}</Typography>;
                            break;
                        }
                        case 'appliedDate': {
                            columnCaption.minWidth = 80;
                            columnCaption.renderCell = (node) => <Typography variant="body2">{Moment(node.appliedDate).format(window._jvselfUser.locale.userDate.shortDate().toUpperCase())}</Typography>;
                            break;
                        }
                        case 'job': {
                            columnCaption.minWidth = 80;
                            columnCaption.renderCell = (node) => (
                              <>
                                {node.applicationCount > 1 ? (
                                  <Link variant="body2" underline="none" onClick={() => setCandidatePreview(node)}>
                                    {node.jobTitle}
                                    { node.applicationCount && node.applicationCount > 1 && (
                                      <Typography>
                                        {props.translations.APPLICANT_QUICKVIEW__0_MORE.replace('{0}', node.applicationCount - 1)}
                                      </Typography>
                                    )}
                                  </Link>
                                ): (
                                  <Typography variant="body2">{node.jobTitle}</Typography>
                                ) }
                              </>
                            );
                            break;
                        }
                        case 'candidateState': {
                            columnCaption.minWidth = 80;
                            columnCaption.renderCell = (node) => <Typography variant="body2">{node.candidateState.title}</Typography>;
                            break;
                        }
                        case 'rankScore': {
                          columnCaption.minWidth = 120;

                          columnCaption.renderCell = (node) => (
                            <>
                              {node.rankScore != null ? (
                                  node.rankScore >= 4 ?
                                  <Chip label={props.translations.COMMON_MATCH} sx={{ backgroundColor: '#8237E2', color: 'white' }} />
                                  : null
                              ): (
                                  <Typography>{props.translations.COMMON_PENDING}</Typography>
                              )}
                            </>
                          );
                          break;
                        }
                        case 'cellPhone':
                          columnCaption.minWidth = 80;
                          columnCaption.renderCell = (node) => <Typography variant="body2">{node.mobile}</Typography>;
                          break;
                        default: {
                            columnCaption.minWidth = 80;
                            columnCaption.renderCell = (node) => {
                              const cellValue = getColumnValueFromSearchNode(node, column.searchId);
                              return <Typography variant="body2">{cellValue || ''}</Typography>;
                            }
                            break;
                        }
                    }
                    map.push(columnCaption);
                }
            });
            return map;
        }
        function columnSortOrder(sorting: Array<object>) {
          let sortList: Array<SortOrderProps> = [];
          sorting.forEach((sort: SortOrderProps) => {
            sortList.push({
              id: sort.searchId,
              label: sort.name,
              directions: ["asc", "desc"],
            });
          });
          setSortOrders(sortList);
        }
        async function fetchConfig() {
          props.configService.get().then(function (data: any) {
            setActionsData(data.actions);
            setPermissions(data.perms);
            setColumnList(createColumnList(data.columnHeaders));
            columnSortOrder(columnWithSort);
            const allAvailableAppStatusFilterOptions: Array<{ key: string, value: string }> = data.standardSearchFilters.find((e: any) => e.searchId === 'workflowStateId').values;
            if (allAvailableAppStatusFilterOptions) {
              setFilterAppStatusFilterValues(allAvailableAppStatusFilterOptions.map((item: { key: string, value: string }) => ({ value: item.key, label: item.value })));
            }
            const allAvailableOverallCompatibilityFilterOptions: Array<{ key: string, value: string }> = data.standardSearchFilters.find((e: any) => e.searchId === 'overallCompatibility').values;
            if (allAvailableAppStatusFilterOptions) {
              setFilterOverallCompatibility(allAvailableOverallCompatibilityFilterOptions.map((item: { key: string, value: string }) => ({ value: item.key, label: item.value })));
            }
          });
        }
        fetchConfig();
    },[]);

    useEffect(() => {
      const tableElement: HTMLElement = document.querySelector('.jv-candidate-list-wrapper.sticky .MuiTableContainer-root');
      if (tableElement && !resultsLoading && !scrollEventAttachedToTable) {
        tableElement.addEventListener('scroll', function() {
          tableElement.classList.toggle('show-sticky-column-shadow', !!tableElement.scrollLeft);
        });
        setScrollEventAttachedToTable(true);
      }
    }, [resultsLoading, scrollEventAttachedToTable])

  const handleSelection = useCallback((node: SelectedNodeItem, nextSelectedState: boolean) => {
       setMoveCandStatusBar(false);
       setClickedAction({name: '', payLoadName: ''})
    if (nextSelectedState) {
        setSelectedCandidate([...selectedCandidate, node.EId]);
        setSelectedCandidateDetails([...selectedCandidateDetails, node]);
    } else {
        setSelectedCandidate(selectedCandidate.filter(item => item !== node.EId))
        setSelectedCandidateDetails(selectedCandidateDetails.filter(item => item.EId !== node.EId))
    }
  }, [selectedCandidate]);

  const handleSelectAll: OnSelectAllChangeEvent = (event:any, eventType:any, context:any) => {
        setClickedAction({name: '', payLoadName: ''})
    if (eventType === 'selectAll') {
        setSelectedCandidate(results.nodes.map(node => node.EId))
        setSelectedCandidateDetails(results.nodes)
    } else if (eventType === 'unselectAll') {
      setSelectedCandidate([])
      setSelectedCandidateDetails([])
    }
  }

   useEffect(() => {
     setIsCandidateValidForMove(
         validateDuplicateCandidates(selectedCandidate, results.nodes)
     );
     if (selectedCandidateDetails.length > 0 && !permissions.skipWorkflowState) {
         const firstStatus = selectedCandidateDetails[0].candidateState.EId;
         const canChangeStatus = selectedCandidateDetails.every(item => item.candidateState.EId === firstStatus);
         setCandidateCanChangeStatus(canChangeStatus);
     } else {
         setCandidateCanChangeStatus(true);
     }

   }, [selectedCandidate]);

  useEffect(() => {
    const actionsEvent = (
      event: any,
      data: any,
      actionKey: any,
      type: string
    ) => {
      setMoveCandStatusBar(false);
      if (actionKey.name === "MOVE_CANDIDATE" && !isCandidateValidForMove) {
        setMoveCandStatusBar(true);
      } else {
          setOpen(true);
          setSelectType(type);
          setClickedAction(actionKey);
          type === "single" && setSelectedCandidate([data.EId]);
      }
    };
    const transformActions = (actions: any, type: string) => {
      // Adding list of action menus required for MVP,
      // un-comment the other actions as required
      const candidateActions = [
        { name: "SEND_NOTE", payLoadName: "sendNote" },
        { name: "ADD_TO_REQUISITION", payLoadName: "addToReq" },
        { name: "CHANGE_STATUS", payLoadName: "changeStatus" },
        { name: "REJECT", payLoadName: "rejectCandidate" },
        { name: "SEND_MESSAGE", payLoadName: "sendMessage" },
        { name: "MOVE_CANDIDATE", payLoadName: "moveCandidate" },
        // {name:'DELETE', payLoadName:'deleteCandidate'},
        // {name:'ADD_TO_COLLECTION', payLoadName:'addToCollection'},
        // {name:'SEND_VIDEO_INTERVIEW', payLoadName:''},
        // {name:'DOWNLOAD_RESUMES', payLoadName:'downloadResume'},
        // {name:'SEEK_CONSENT', payLoadName:''},
      ];
      const actionLinks: any = [];
      Object.keys(actions).forEach((key) => {
        let filteredAction = candidateActions.filter((e) => e.name === key);
        if (filteredAction.length > 0) {
          let actionTertiaryLink = {
            type: "tertiary",
            label: actions[key],
            onClick: (event: any, data: any) =>
              actionsEvent(event, data, filteredAction[0], type),
          };
          actionLinks.push(actionTertiaryLink);
        }
      });
      return actionLinks;
    };
    if (Object.keys(actionsData).length > 0) {
      setActionsList(transformActions(actionsData, "single"));
      setGlobalActionsList(transformActions(actionsData, "multiple"));
    }
  }, [actionsData, isCandidateValidForMove]);

    const handleSearch = (props: ListSearchChangeProps) => {
      let filterData;
      let checkFilterCondition = false;
      if (props.appliedSearchFilters.length > 0 || props.q && props.q.length) {
        const filteredAppliedSearchFilters = filterDuplicateFilters(props.appliedSearchFilters)

        filterData = transformFilterData({
          ...props,
          appliedSearchFilters: filteredAppliedSearchFilters,
        });

        setAppliedSearchFilters(setActiveFilterChip({ appliedSearchFilters: filteredAppliedSearchFilters }));
        checkFilterCondition =
          filterData &&
          filterData.lastUpdated !== null &&
          Object.keys(filterData.lastUpdated).length > 0;
      } else {
        setAppliedSearchFilters([]);
      }
      setCandPayload({
        ...CandPayload,
        page: 0,
        keywordSearch: {
          keyword: filterData ? filterData.keywords : "",
          searchNameOnly: false,
          searchNotesOnly: false,
          searchMessagesOnly: false,
          searchResumeOnly: false,
          exactMatch: false,
          typeAhead: false,
        },
        searchType: "all",
        lastUpdated: checkFilterCondition ? filterData.lastUpdated : null,
        workflowStateId: filterData ? filterData.workflowStateId : null,
        overallCompatibility: filterData ? filterData.overallCompatibility : [],
        keywords: filterData ? filterData.keywords : "",
        order: props.appliedSortOrder.direction,
        sortBy: [
          {
            fieldId: props.appliedSortOrder.sortOrder.id,
          },
        ],
      });
    };

    const [perPageCount, setPerPageCount] = useState(0);
    const [totalCandidates, setTotalCandidates] = useState(0);
    const [pagingContext, setPagingContext] = useState({
      hideFirstLastActions: true,
      perPage: perPageCount,
      page: 0,
      first: 0,
      last: totalCandidates <= perPageCount ? totalCandidates : perPageCount,
    });
    const onPaginationChange = (event: any, currPage: number) => {
      setCandPayload({
        ...CandPayload,
        page: currPage - 1,
      });
    };
    const theme = createInternalTheme({ darkMode: false });

    return (
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <DomainProvider urls={window.jvUrls}>
            <JobDetailsProvider jobDetails={props.jobDetails}>
              <LocalizationProvider locale={window._jvselfUser.locale.languageTag} translations={props.translations as LocalizationValues}>
                <ThemeProvider theme={theme}>
                  <CandidateServiceProvider candidateService={props.candidateService}>
                    <SnackbarServiceProvider maxSnack={5}>
                      <CandidateListContext.Provider value={{reqsEId, modalStyle, props, permissions, candidateCanChangeStatus, selectedCandidate, selectedCandidateDetails,validatedCandidateList, setValidatedCandidateList}}>
                        <LayoutRoot>
                          <LayoutRootSection top bottom transparent>
                              {moveCandStatusBar && <StatusMessage sx={{mb: 2}} type="warning">{props.translations.JV_MOVE_CANDIDATE_ERROR}</StatusMessage>}
                              {(clickedAction.name === "CHANGE_STATUS" && !candidateCanChangeStatus) &&
                                  <StatusMessage sx={{mb: 2}} type="warning">{props.translations.JV_APPLICANT_CHANGE_STATUS_REQUISITION_ERROR}</StatusMessage>
                              }
                              <ListBase
                                  selectable
                                  showSearch
                                  labelSearch={props.translations.SEARCH}
                                  labelSearchAria={props.translations.SEARCH_CANDIDATE}
                                  sortOrders={sortOrders}
                                  appliedSortOrder={appliedSortOrder}
                                  onSearch={handleSearch}
                                  // @ts-ignore
                                  searchFilters={searchFilters}
                                  appliedSearchFilters={appliedSearchFilters}
                                  results={results}
                                  pagingContext={pagingContext}
                                  onPageChanged = {onPaginationChange}
                                  resultsPerPage = {perPageCount}
                                  selectedIds={selectedCandidate}
                                  onSelection={handleSelection}
                                  onSelectAll={handleSelectAll}
                                  allSelected={selectedCandidate.length === results.nodes.length}
                                  SelectedActionGroupProps={{
                                    id: 'global-select',
                                    variant: 'button_more',
                                    actionLabel: 'Actions',
                                    actions: globalActionsList
                                  }}
                                  resultsLoading={resultsLoading && results.nodes.length === 0}
                                  viewContexts={[
                                      {
                                          ariaLabel: props.translations.DATATABLE_VIEW,
                                          icon: <TableChartIcon />,
                                          renderResults: (viewContextProps) => (
                                            results.nodes.length === 0 ? (
                                              <Paper>
                                                  <Box padding={2} alignItems="center">
                                                      <LocalizedTypography align="center" text={LocalizedText.I18N_NO_RESULTS_FOUND} />
                                                  </Box>
                                              </Paper>
                                            ): (
                                              <DataTable
                                                  {...viewContextProps}
                                                  getRowID={node => node.EId}
                                                  columns={columnList}
                                                  renderActions={(node) => (
                                                      <ActionGroup id="primary-buttons-actions" actions={actionsList} data={node}/>
                                                  )}
                                                  labelCheckboxAriaLabel= {props.translations.SELECT_CANDIDATE}
                                                  labelCheckboxKey="name"
                                              />
                                            )
                                          ),
                                          renderLoading: (viewContextProps) => (
                                            <ListBaseDefaultLoading />
                                          ),
                                      },
                                      // Commenting as part of PIXEL-658
                                      // {
                                      //     ariaLabel: 'Card View',
                                      //     icon: <ArticleIcon />,
                                      //     component: (viewContextProps) => (
                                      //         <ListItemCards
                                      //             {...viewContextProps}
                                      //             getRowID={node => node.EId}
                                      //             getTitle={(node => node.fullName)}
                                      //             getCheckboxAriaLabel={node => `${props.translations.SELECT_CANDIDATE + node.fullName}`}
                                      //             renderTitleAdornment={(node) => (
                                      //                 <Chip label= {props.translations.STRONG_MATCH} color="primary" />
                                      //             )}
                                      //             renderContent={(node) => (
                                      //                 <>
                                      //                     <LabelValueLayout divider={false}>
                                      //                         <LabelValueBase label={props.translations.APPLIED_FOR} children={<Link underline="hover" sx={{ fontWeight: 600 }}>{node.jobTitle}</Link>} />
                                      //                         <LabelValueBase label={props.translations.WORKFLOW_STATUS} children={<Typography variant="body2" sx={{ fontWeight: 600 }}>{node.candidateState.title}</Typography>} />
                                      //                         <LabelValueBase label={props.translations.EMAIL} children={node.email} />
                                      //                     </LabelValueLayout>
                                      //                     <LabelValueLayout divider={false}>
                                      //                         <LabelValueBase label={props.translations.CURRENT_TITLE} children={node.currentTitle} />
                                      //                         <LabelValueBase label={props.translations.LAST_UPDATED} children={Moment(node.lastActivity).format(window._jvselfUser.locale.userDate.shortDate().toUpperCase())} />
                                      //                         <LabelValueBase label={props.translations.PHONE} children={node.homePhone} />
                                      //                     </LabelValueLayout>
                                      //                     <LabelValueLayout divider={false}>
                                      //                         <LabelValueBase label={props.translations.WORK_HISTORY} children={node.workHistory ? node.workHistory[0].companyName + ' ' +  node.workHistory[0].startYear + ' - ' + node.workHistory[0].endYear : ''} />
                                      //                         <LabelValueBase label={props.translations.SOURCE} children={node.source} />
                                      //                         <LabelValueBase label={props.translations.LOCATION} children={node.country} />
                                      //                     </LabelValueLayout>
                                      //                 </>
                                      //             )}
                                      //             renderActions={(node) => (
                                      //               <ActionGroup id="primary-buttons-actions" actions={actionsList} data={node}/>
                                      //             )}
                                      //         />
                                      //     )
                                      // }
                                  ]}
                              />
                          </LayoutRootSection>
                        </LayoutRoot>
                        <CandidateActionsModal
                          open={open}
                          setOpenModal={setOpen}
                          candidateService={props.candidateService}
                          openAction={clickedAction}
                          selectType={selectType}
                          resetOpenAction={() => setClickedAction({name: '', payLoadName: ''})}
                        />
                        <PreviewCandidateModal
                          open={Boolean(candidatePreview)}
                          onClose={() => {
                            setCandidatePreview(null)
                          }}
                          candidateApplication={candidatePreview}
                        />
                      </CandidateListContext.Provider>
                    </SnackbarServiceProvider>
                  </CandidateServiceProvider>
                </ThemeProvider>
              </LocalizationProvider>
            </JobDetailsProvider>
          </DomainProvider>
        </QueryClientProvider>
      </StrictMode>
    );
};

export { react2angular } from "../../lib/react2Angular";
export default App;
